import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getEpisodeProgress, getProductById, updateEpisodeProgress } from '@/api/api'
import {
  createHighlight,
  deleteHighlight,
  getEpisodeHighlights,
  type Highlight,
  updateHighlight,
} from '@/api/services/highlight'
import type { EpisodeList, Product } from '@/api/type'
import { Header } from '@/components/shared/header'
import type { ActionSection } from '@/components/ui/custom-dropdown'
import { Spinner } from '@/components/ui/spinner'
import { useProductDownload } from '@/hooks/useEpisodeDownload'
import { useMediaSession } from '@/hooks/useMediaSession'
import { useOfflineUrl } from '@/hooks/useOfflineUrl'
import { useHasActiveDownload, useIsProductOffline, useRemoveProductOffline } from '@/hooks/useProductDownloadStatus'
import { numberCodec, useQueryState } from '@/hooks/useQueryState'
import { useQueryTabState } from '@/hooks/useQueryTabState'
import { useSignedUrl } from '@/hooks/useSignedUrl'
import { type Chapter, getCurrentChapterIndex, parseCueSheet } from '@/lib/cue-parser'
import { parseUtcDate } from '@/lib/date-utils'
import { buildDownloadItems } from '@/lib/download-utils'
import { clearCachedPosition, readCachedPosition, writeCachedPosition } from '@/lib/playback-position-cache'
import { useProgressStore } from '@/store/useProgressStore'
import type { AudiobookTranscript } from '@/types/audiobook-transcript'

import { PdfReader } from '../pdf/pdf-reader'
import { AudioControls } from './audio-controls'
import { MiniPlayer } from './mini-player'
import { TranscriptView } from './transcript-view'

const PROGRESS_SAVE_DEBOUNCE = 2000
// pending seek と audio.currentTime の一致判定に使う許容誤差。
// - applyPendingSeek: currentTime が target 付近に既に到達しているかの判定
// - handleSeeked: ブラウザの seek 完了と target 一致の判定
// 両方で同じ値を使うことで意図を揃える
const SEEK_MATCH_TOLERANCE_SEC = 0.5
// localStorage への位置ミラーの間隔（秒）。timeupdate は毎秒 4 回程度発火するため間引く
const POSITION_CACHE_INTERVAL_SEC = 1
// 復元失敗（先頭から再生）の判定しきい値と再シークの上限回数。
// キーフレーム前に丸められる程度のズレで再シークを繰り返さないよう幅を取る
const RESTORE_VERIFY_TOLERANCE_SEC = 5
const MAX_RESTORE_RETRY = 5

type Props = {
  url: string
  title: string
  onClose: () => void
  onInfoClick: () => void
  episodeId?: string
  productIdNum?: number
}

type Tab = 'player' | 'pdf' | 'transcript'

const TABS: readonly Tab[] = ['player', 'pdf', 'transcript']

// 再生速度の許容範囲は playback-speed-sheet のスライダーと揃える
const PLAYBACK_RATE_STORAGE_KEY = 'audiobook-playback-rate'
const MIN_PLAYBACK_RATE = 0.5
const MAX_PLAYBACK_RATE = 3.0

/** localStorage に保存された端末ごとの既定速度（未保存・不正値なら 1.0） */
const readStoredPlaybackRate = (): number => {
  const stored = localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY)
  if (!stored) return 1.0
  const parsed = parseFloat(stored)
  if (!Number.isFinite(parsed)) return 1.0
  return Math.min(MAX_PLAYBACK_RATE, Math.max(MIN_PLAYBACK_RATE, parsed))
}

/** オーディオブック用のエピソードを分類 */
const classifyEpisodes = (episodes: EpisodeList[]) => {
  let audioEpisode: EpisodeList | undefined
  let cueEpisode: EpisodeList | undefined
  let pdfEpisode: EpisodeList | undefined
  let transcriptEpisode: EpisodeList | undefined

  for (const ep of episodes) {
    if (ep.mime_type === 'audio/mpeg') audioEpisode = ep
    else if (ep.mime_type === 'application/x-cue') cueEpisode = ep
    else if (ep.mime_type === 'application/pdf') pdfEpisode = ep
    else if (ep.mime_type === 'application/json') transcriptEpisode = ep
  }

  return { audioEpisode, cueEpisode, pdfEpisode, transcriptEpisode }
}

export const AudioBookPlayer = ({ url, title, onClose, onInfoClick, episodeId, productIdNum }: Props) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const lastSavedProgressRef = useRef(0)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // localStorage への位置ミラーの最終書き込み位置（書き込み頻度の間引き用）
  const lastCachedTimeRef = useRef(0)
  // 最後に観測した再生位置と尺（audio 要素が参照できない場面のフォールバック用）
  const lastKnownRef = useRef({ time: 0, duration: 0 })
  // ブラウザによっては loadedmetadata 直後の audio.currentTime 代入が
  // UI には反映されてもデコーダに届かず、再生しても 0 から始まることがある。
  // seek を複数のライフサイクルイベント（loadedmetadata / canplay / play 直前）で適用し、
  // seeked で目標到達を確認できたら null にする
  const pendingSeekRef = useRef<number | null>(null)
  // 復元位置へ本当に到達したかを再生中の currentTime で検証するための目標値。
  // blob: URL は Media Fragment が効かず、iOS では loadedmetadata / canplay 時の seek を
  // デコーダが取りこぼして先頭から再生されることがある
  const restoreVerifyRef = useRef<{ target: number; attempts: number } | null>(null)

  // タブ・再生速度は URL クエリで保持し、リロードや共有時に復元できるようにする
  const [activeTab, setActiveTab] = useQueryTabState<Tab>('tab', TABS, 'player')
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  // 端末ごとの既定速度を codec のデフォルトにすることで、既定と同じ間は URL を汚さない
  const playbackRateCodec = useMemo(
    () =>
      numberCodec(readStoredPlaybackRate(), {
        min: MIN_PLAYBACK_RATE,
        max: MAX_PLAYBACK_RATE,
        precision: 2,
      }),
    [],
  )
  const [playbackRate, setPlaybackRate] = useQueryState('rate', playbackRateCodec)
  const [chapters, setChapters] = useState<Chapter[]>([])
  const [product, setProduct] = useState<Product | null>(null)
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>()
  const [bookmarks, setBookmarks] = useState<Highlight[]>([])
  // src に #t= として埋め込む初回開始位置（Media Fragment URI）
  // iOS Safari は大きなオーディオに対して loadedmetadata 後のプログラマチックな seek を
  // 取りこぼすことがある（currentTime は反映されるが実際のデコーダは 0 から再生）ので、
  // 初回ロード時に Media Fragment で位置を伝えることが確実
  const [srcSeek, setSrcSeek] = useState<number | null>(null)
  const [progressLoadedEpisodeId, setProgressLoadedEpisodeId] = useState<string | null>(null)
  // audio の loadedmetadata が発火し currentTime が src の #t= になったか
  // これが false の間はスライダー位置が 0 のままなので、復元中オーバーレイを継続
  const [metadataLoaded, setMetadataLoaded] = useState(false)
  // オフラインファースト署名付きURL（IndexedDB → サーバー）
  const numericEpisodeId = useMemo(() => {
    if (!episodeId) return null
    const parsed = Number(episodeId)
    return Number.isNaN(parsed) ? null : parsed
  }, [episodeId])
  const { url: audioUrl, isOffline: isAudioOffline } = useOfflineUrl(numericEpisodeId, url)

  // プロダクト情報を取得してエピソード分類
  const { cueEpisode, pdfEpisode, transcriptEpisode } = useMemo(() => {
    if (!product) return { cueEpisode: undefined, pdfEpisode: undefined, transcriptEpisode: undefined }
    return classifyEpisodes(product.episodes)
  }, [product])

  // CUE用のオフラインファーストURL
  const cueEpisodeId = cueEpisode?.episode_id ?? null
  const { url: cueUrl } = useOfflineUrl(cueEpisodeId, cueEpisode ? `/e/${cueEpisode.episode_id}/stream` : null)

  // PDF用のオフラインファーストURL
  const pdfEpisodeId = pdfEpisode?.episode_id ?? null
  const pdfStreamPath = pdfEpisode ? `/e/${pdfEpisode.episode_id}/stream` : null
  const { url: pdfOfflineUrl, isOffline: isPdfOffline } = useOfflineUrl(pdfEpisodeId, pdfStreamPath)

  // Transcript用のオフラインファーストURL
  const transcriptEpisodeId = transcriptEpisode?.episode_id ?? null
  const transcriptStreamPath = transcriptEpisode ? `/e/${transcriptEpisode.episode_id}/stream` : null
  const { url: transcriptUrl } = useOfflineUrl(transcriptEpisodeId, transcriptStreamPath)

  // Transcriptデータのfetch
  const [transcript, setTranscript] = useState<AudiobookTranscript | null>(null)
  useEffect(() => {
    if (!transcriptUrl) {
      setTranscript(null)
      return
    }
    const controller = new AbortController()
    fetch(transcriptUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data: AudiobookTranscript) => setTranscript(data))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        console.error('Failed to fetch transcript:', err)
      })
    return () => controller.abort()
  }, [transcriptUrl])

  // サムネイル署名付きURL
  const { url: signedThumbnailUrl } = useSignedUrl(product?.thumbnail ?? null)

  useEffect(() => {
    if (signedThumbnailUrl) setThumbnailUrl(signedThumbnailUrl)
  }, [signedThumbnailUrl])

  // プロダクト取得
  useEffect(() => {
    if (!productIdNum) return
    getProductById(productIdNum).then(setProduct).catch(console.error)
  }, [productIdNum])

  // CUEシートのフェッチ・パース
  useEffect(() => {
    if (!cueUrl) return
    fetch(cueUrl)
      .then((res) => res.text())
      .then((text) => setChapters(parseCueSheet(text)))
      .catch(console.error)
  }, [cueUrl])

  // ブックマーク取得
  useEffect(() => {
    if (!episodeId) return
    getEpisodeHighlights(Number(episodeId)).then(setBookmarks).catch(console.error)
  }, [episodeId])

  // ブックマーク追加（メモ付き）
  const handleAddBookmark = useCallback(
    async (note: string) => {
      if (!episodeId) return
      const audio = audioRef.current
      if (!audio) return

      const timestamp = Math.floor(audio.currentTime)
      const chapterIndex = getCurrentChapterIndex(chapters, audio.currentTime)
      const chapterTitle = chapters[chapterIndex]?.title

      try {
        const newBookmark = await createHighlight({
          episode_id: Number(episodeId),
          media_type: 'audio',
          position_data: { timestamp },
          text: chapterTitle ?? null,
          note: note || undefined,
        })
        setBookmarks((prev) => [...prev, newBookmark])
      } catch (error) {
        console.error('[Bookmark] Failed to add bookmark:', error)
      }
    },
    [episodeId, chapters],
  )

  // ブックマークメモ更新
  const handleUpdateBookmarkNote = useCallback(async (bookmarkId: number, note: string) => {
    try {
      const updated = await updateHighlight(bookmarkId, { note })
      setBookmarks((prev) => prev.map((bm) => (bm.id === bookmarkId ? updated : bm)))
    } catch (error) {
      console.error('[Bookmark] Failed to update note:', error)
    }
  }, [])

  // ブックマーク削除
  const handleDeleteBookmark = useCallback(async (bookmarkId: number) => {
    try {
      await deleteHighlight(bookmarkId)
      setBookmarks((prev) => prev.filter((bm) => bm.id !== bookmarkId))
    } catch (error) {
      console.error('[Bookmark] Failed to delete bookmark:', error)
    }
  }, [])

  // 進捗復元: API から保存済み位置を取得し srcSeek にセット（src の #t= として適用）
  // 先に progress を取得してから audio のロードを開始するため、progressLoadedEpisodeId を gate にする。
  // boolean では state 更新が次の render まで反映されず、エピソード切替直後の最初の render で
  // 前エピソードの ready 状態が漏れて src が先行セットされるため、episodeId と紐付けて管理する
  useEffect(() => {
    if (!episodeId) {
      setProgressLoadedEpisodeId(null)
      setSrcSeek(null)
      setMetadataLoaded(false)
      pendingSeekRef.current = null
      restoreVerifyRef.current = null
      return
    }
    setSrcSeek(null)
    setMetadataLoaded(false)
    pendingSeekRef.current = null
    restoreVerifyRef.current = null
    lastCachedTimeRef.current = 0
    lastKnownRef.current = { time: 0, duration: 0 }
    let cancelled = false

    // API がハングしても再生不能にならないよう、最大 5 秒待ったら gate を開く
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setProgressLoadedEpisodeId(episodeId)
    }, 5000)

    // 復元位置を適用する（サーバー / ローカルミラーのどちらでも同じ経路を通す）
    const applyRestorePosition = (position: number) => {
      // src に #t= として埋め込むため、progressLoadedEpisodeId セット前に
      // srcSeek を先に反映しておくことで audio src が初回から #t= 付きになる
      setSrcSeek(position)
      pendingSeekRef.current = position
      restoreVerifyRef.current = { target: position, attempts: 0 }
      lastSavedProgressRef.current = position
      lastCachedTimeRef.current = position
    }

    // iOS のスリープ中はネットワーク保存が届かないままプロセスが kill されることがある。
    // localStorage のミラーの方が新しければそちらを採用して巻き戻りを防ぐ
    const cached = readCachedPosition(Number(episodeId))

    getEpisodeProgress(Number(episodeId))
      .then((progress) => {
        if (cancelled) return
        if (progress?.is_completed) {
          clearCachedPosition(Number(episodeId))
          return
        }
        const serverUpdatedAt = progress ? parseUtcDate(progress.updated_at).getTime() : 0
        const useCached = !!cached && cached.position > 0 && cached.updatedAt > serverUpdatedAt
        if (useCached) {
          applyRestorePosition(cached.position)
          return
        }
        if (progress && progress.progress > 0) applyRestorePosition(progress.progress)
      })
      .catch(() => {
        if (cancelled || !cached || cached.position <= 0) return
        applyRestorePosition(cached.position)
      })
      .finally(() => {
        if (cancelled) return
        clearTimeout(fallbackTimer)
        setProgressLoadedEpisodeId(episodeId)
      })

    return () => {
      cancelled = true
      clearTimeout(fallbackTimer)
    }
  }, [episodeId])

  // pending seek を audio に適用する。
  // - 目標位置に既に到達していれば（オンライン #t=SECONDS のケース）その場で pending をクリアして終了。
  //   これをスキップすると seek が発火しないため seeked でクリアされず、後の play 操作で現在位置が復元位置に巻き戻されるバグになる。
  // - 目標位置と離れていれば実際に seek し、完了確認は seeked イベントに任せる。
  const applyPendingSeek = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    const target = pendingSeekRef.current
    if (target === null || target <= 0) return
    if (!audio.duration || Number.isNaN(audio.duration)) return
    if (Math.abs(audio.currentTime - target) < SEEK_MATCH_TOLERANCE_SEC) {
      pendingSeekRef.current = null
      return
    }
    audio.currentTime = target
  }, [])

  // audio 要素の準備完了時に duration / playbackRate / 初期 currentTime をセット
  // オンライン (HTTP URL) は src の #t=SECONDS で復元位置に到達済みだが、
  // オフライン (blob: URL) は Media Fragment がブラウザに無視されるため、
  // pendingSeekRef によるプログラマティック seek で復元する
  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    setDuration(audio.duration)
    audio.playbackRate = playbackRate
    applyPendingSeek()
    setCurrentTime(audio.currentTime)
    lastKnownRef.current = { time: audio.currentTime, duration: audio.duration }
    setMetadataLoaded(true)
  }, [playbackRate, applyPendingSeek])

  // canplay はデコーダが再生可能になったタイミング。loadedmetadata での seek が
  // デコーダに届かなかった場合 (iOS Safari / 一部の blob URL ケース) でもここで回復できる。
  // blob: URL はシーク可能になるタイミングが遅いため loadeddata / playing でも適用を試みる
  const handleCanPlay = useCallback(() => {
    applyPendingSeek()
  }, [applyPendingSeek])

  // seek が目標位置に到達したら pending をクリア
  const handleSeeked = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    const target = pendingSeekRef.current
    if (target !== null && Math.abs(audio.currentTime - target) < SEEK_MATCH_TOLERANCE_SEC) {
      pendingSeekRef.current = null
    }
    setCurrentTime(audio.currentTime)
    // 一時停止中の手動シークは timeupdate が来ないため、ここでミラーを更新しておく
    if (episodeId && pendingSeekRef.current === null) {
      lastKnownRef.current = { time: audio.currentTime, duration: lastKnownRef.current.duration }
      lastCachedTimeRef.current = audio.currentTime
      writeCachedPosition(Number(episodeId), audio.currentTime)
    }
  }, [episodeId])

  // 進捗保存
  const saveProgress = useCallback(
    async (progressSeconds: number, totalSeconds: number, incrementPlayCount = false) => {
      if (!episodeId || totalSeconds <= 0) return
      const isCompleted = progressSeconds >= totalSeconds - 1
      if (isCompleted) clearCachedPosition(Number(episodeId))
      // 一覧画面（ProductContainer など）の表示を即座に合わせる
      useProgressStore.getState().applyEpisodeProgress(Number(episodeId), Math.floor(progressSeconds), isCompleted)
      try {
        await updateEpisodeProgress(Number(episodeId), {
          progress: Math.floor(progressSeconds),
          is_completed: isCompleted,
          increment_play_count: incrementPlayCount,
        })
      } catch (error) {
        console.error('[Progress] ❌ Failed to save audiobook progress:', error)
      }
    },
    [episodeId],
  )

  const debouncedSaveProgress = useCallback(
    (progressSeconds: number, totalSeconds: number) => {
      const diff = Math.abs(progressSeconds - lastSavedProgressRef.current)
      if (diff < 5) return
      if (saveTimeoutRef.current) return

      saveTimeoutRef.current = setTimeout(() => {
        lastSavedProgressRef.current = progressSeconds
        saveProgress(progressSeconds, totalSeconds)
        saveTimeoutRef.current = null
      }, PROGRESS_SAVE_DEBOUNCE)
    },
    [saveProgress],
  )

  /** 保留中の保存を即時確定させる（一時停止・アプリ離脱・復帰時に位置を取りこぼさないため） */
  const flushProgress = useCallback(() => {
    // アンマウント時は React が ref を外した後に呼ばれるため、最後に観測した値へフォールバックする
    const audio = audioRef.current
    const time = audio ? audio.currentTime : lastKnownRef.current.time
    const total = audio && audio.duration > 0 ? audio.duration : lastKnownRef.current.duration
    if (!(total > 0)) return
    // 復元待ちの間は先頭付近の位置を書き込んでしまうので保存しない
    if (pendingSeekRef.current !== null) return
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    if (episodeId) writeCachedPosition(Number(episodeId), time)
    if (Math.abs(time - lastSavedProgressRef.current) < 1) return
    lastSavedProgressRef.current = time
    void saveProgress(time, total)
  }, [episodeId, saveProgress])

  // iOS でスリープすると JS タイマーが止まりネットワーク保存も届かないまま PWA が kill されうる。
  // 離脱直前（hidden / pagehide）と復帰直後（visible）の両方で位置を確定保存する。
  // 復帰直後の保存は、バックグラウンド再生中に届かなかった分をここで取り戻すためのもの
  useEffect(() => {
    const handleVisibilityChange = () => {
      flushProgress()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', flushProgress)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', flushProgress)
    }
  }, [flushProgress])

  // timeupdate
  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    setCurrentTime(audio.currentTime)
    if (!(audio.duration > 0)) return
    // 実際に進んだ位置で復元の成否を確かめる。
    // 先頭付近から再生されていたら seek がデコーダに届いていないのでやり直す
    const verify = restoreVerifyRef.current
    if (verify) {
      if (audio.currentTime >= verify.target - RESTORE_VERIFY_TOLERANCE_SEC) {
        restoreVerifyRef.current = null
      } else if (verify.attempts < MAX_RESTORE_RETRY) {
        verify.attempts += 1
        pendingSeekRef.current = verify.target
        audio.currentTime = verify.target
        return
      } else {
        // 諦めた後も pending は残す。0 付近の位置を保存すると保存済みの再生位置が壊れて戻せなくなる
        // （手動シークで pending がクリアされ、そこから通常の保存が再開する）
        console.warn('[AudioPlayer] 再生位置の復元に失敗しました:', verify.target)
        restoreVerifyRef.current = null
      }
    }
    // 復元・再ロード待ちの間は先頭付近の位置を書き込んでしまうので保存しない
    if (pendingSeekRef.current !== null) return
    lastKnownRef.current = { time: audio.currentTime, duration: audio.duration }
    // localStorage への同期書き込みはプロセス kill されても残るので、
    // サーバー保存が届かなかった場合の巻き戻り防止線になる
    if (episodeId && Math.abs(audio.currentTime - lastCachedTimeRef.current) >= POSITION_CACHE_INTERVAL_SEC) {
      lastCachedTimeRef.current = audio.currentTime
      writeCachedPosition(Number(episodeId), audio.currentTime)
    }
    debouncedSaveProgress(audio.currentTime, audio.duration)
  }, [episodeId, debouncedSaveProgress])

  // ロック画面からの再生リトライ (useMediaSession の audio.load()) やブラウザによる
  // リソース破棄で src が再ロードされると currentTime が src の #t= 位置へ巻き戻る。
  // 直前の位置を pending に積み、復元完了まで進捗保存も止める
  const handleEmptied = useCallback(() => {
    const last = lastKnownRef.current.time
    if (last > 0) pendingSeekRef.current = last
  }, [])

  // play / pause 同期
  // ブラウザによっては canplay でも seek がデコーダに反映されないことがあるため、
  // ユーザーの play 操作直前にも pending seek を適用して少なくともこのタイミングで復元を完了させる
  // audio.play() は Promise を返し、直前の seek による中断で AbortError を投げることがあるため catch で握る
  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      applyPendingSeek()
      audio.play().catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        console.error('[AudioPlayer] play() failed:', err)
      })
    } else {
      audio.pause()
    }
  }, [applyPendingSeek])

  // ユーザーによる手動 seek / スキップは復元 pending を取り消す
  // （このクリアをしないと、この後 canplay が遅延発火した際に復元位置に巻き戻される）
  const handleSeek = useCallback((seconds: number) => {
    const audio = audioRef.current
    if (!audio) return
    pendingSeekRef.current = null
    restoreVerifyRef.current = null
    audio.currentTime = seconds
    setCurrentTime(seconds)
  }, [])

  const handleSkip = useCallback((delta: number) => {
    const audio = audioRef.current
    if (!audio) return
    pendingSeekRef.current = null
    restoreVerifyRef.current = null
    audio.currentTime = Math.max(0, Math.min(audio.currentTime + delta, audio.duration || 0))
    setCurrentTime(audio.currentTime)
  }, [])

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      setPlaybackRate(rate)
      // 端末ごとの既定速度としても保存し、次回以降の初期値にする
      localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, rate.toString())
      if (audioRef.current) {
        audioRef.current.playbackRate = rate
      }
    },
    [setPlaybackRate],
  )

  // 再生・一時停止イベント同期
  const handlePlay = useCallback(() => setPlaying(true), [])
  const handlePause = useCallback(() => {
    setPlaying(false)
    // 一時停止のまま放置されるとブラウザにタブごと破棄されうるので、ここで位置を確定させる
    flushProgress()
  }, [flushProgress])

  const handleEnded = useCallback(() => {
    const audio = audioRef.current
    if (audio && audio.duration > 0) {
      saveProgress(audio.duration, audio.duration, true)
    }
  }, [saveProgress])

  // 速度変更時にaudio要素へ反映
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  // アンマウント時のクリーンアップ（未保存の位置を確定してから破棄する）
  const flushProgressRef = useRef(flushProgress)
  useEffect(() => {
    flushProgressRef.current = flushProgress
  }, [flushProgress])

  useEffect(() => {
    return () => {
      flushProgressRef.current()
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
    }
  }, [])

  // ナレーター情報取得
  const narrator = useMemo(() => {
    if (!product) return undefined
    const narratorTag = product.tags.find((t) => t.type === 'narrator')
    return narratorTag?.name
  }, [product])

  // MediaSession
  useMediaSession({
    title,
    artist: narrator,
    album: 'Audiobook',
    artworkUrl: thumbnailUrl,
    audioRef,
  })

  const hasPdf = !!pdfEpisode
  const hasTranscript = !!transcript
  const showTabs = hasPdf || hasTranscript

  // --- ダウンロード状態管理 ---
  const { downloadProduct } = useProductDownload()
  const allEpisodeIds = useMemo(() => product?.episodes.map((ep) => ep.episode_id) ?? [], [product])
  const hasActiveDownload = useHasActiveDownload(allEpisodeIds)
  const { isFullyOffline, setIsFullyOffline } = useIsProductOffline(allEpisodeIds)
  const handleRemoveOffline = useRemoveProductOffline(productIdNum, setIsFullyOffline)

  const handleDownloadAll = useCallback(() => {
    if (!product || !productIdNum) return
    downloadProduct(buildDownloadItems(product.episodes, productIdNum, 'audiobook'))
  }, [product, productIdNum, downloadProduct])

  // Header の dropdownSections
  const dropdownSections = useMemo<ActionSection[]>(() => {
    if (!product || !productIdNum) return []

    if (hasActiveDownload) {
      return [
        {
          type: 'action' as const,
          label: 'オフライン',
          items: [{ key: 'downloading', label: 'ダウンロード中...' }],
        },
      ]
    }

    if (isFullyOffline) {
      return [
        {
          type: 'action' as const,
          label: 'オフライン',
          items: [{ key: 'remove', label: 'ダウンロードデータを削除', onClick: handleRemoveOffline }],
        },
      ]
    }

    return [
      {
        type: 'action' as const,
        label: 'オフライン',
        items: [{ key: 'download', label: 'オフラインに保存', onClick: handleDownloadAll }],
      },
    ]
  }, [product, productIdNum, hasActiveDownload, isFullyOffline, handleDownloadAll, handleRemoveOffline])

  // Progress 復元完了フラグ：
  // - progress fetch 完了（progressLoadedEpisodeId が現在の episodeId と一致）
  // - かつ srcSeek がない（復元不要）or audio の metadata ロード完了（currentTime が反映済み）
  const progressReady = !episodeId || (progressLoadedEpisodeId === episodeId && (srcSeek === null || metadataLoaded))

  // audio 要素の src 値を計算。オンライン (HTTP) の場合は Media Fragment (#t=SECONDS) を付与して
  // iOS Safari のデコーダにも開始位置を伝える。オフライン (blob:) URL はフラグメントが
  // 効かないため素の URL を返し、handleLoadedMetadata 側で明示 seek で復元する。
  // 一度確定した src は seek 消化後も変えず、音声要素が再ロードされないようにする
  // （srcSeek は消化ではなく episodeId 変更でのみリセット）
  const audioSrc = useMemo(() => {
    if (episodeId && progressLoadedEpisodeId !== episodeId) return undefined
    if (!audioUrl) return undefined
    if (srcSeek === null || srcSeek <= 0) return audioUrl
    if (isAudioOffline) return audioUrl
    const separator = audioUrl.includes('#') ? '&' : '#'
    return `${audioUrl}${separator}t=${srcSeek}`
  }, [episodeId, progressLoadedEpisodeId, audioUrl, srcSeek, isAudioOffline])

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* 永続audio要素（タブ切り替えでも再生継続） */}
      {/* progress 取得完了後（かつ現在の episodeId に紐づく ready 状態）に src を設定して、
          音声ファイルのロードが progress API を遅らせないようにする */}
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleCanPlay}
        onCanPlay={handleCanPlay}
        onPlaying={handleCanPlay}
        onSeeked={handleSeeked}
        onEmptied={handleEmptied}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
      />

      {/* ヘッダー */}
      <Header title={title} onClose={onClose} dropdownSections={dropdownSections} />

      {/* progress 復元が完了するまで Header 以外の操作を抑止 */}
      <div className="relative flex flex-col flex-1 min-h-0">
        {/* タブ切り替え（PDF or Transcriptがある場合に表示） */}
        {showTabs && (
          <div className="flex border-b border-border">
            <button
              type="button"
              onClick={() => setActiveTab('player')}
              className={`flex-1 py-2 text-sm font-medium text-center transition-colors ${
                activeTab === 'player' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
              }`}
            >
              再生
            </button>
            {hasTranscript && (
              <button
                type="button"
                onClick={() => setActiveTab('transcript')}
                className={`flex-1 py-2 text-sm font-medium text-center transition-colors ${
                  activeTab === 'transcript' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
                }`}
              >
                テキスト
              </button>
            )}
            {hasPdf && (
              <button
                type="button"
                onClick={() => setActiveTab('pdf')}
                className={`flex-1 py-2 text-sm font-medium text-center transition-colors ${
                  activeTab === 'pdf' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
                }`}
              >
                PDF
              </button>
            )}
          </div>
        )}

        {/* コンテンツ */}
        <div className="flex-1 overflow-hidden relative">
          {activeTab === 'player' && (
            <AudioControls
              title={title}
              narrator={narrator}
              thumbnailUrl={thumbnailUrl}
              playing={playing}
              currentTime={currentTime}
              duration={duration}
              playbackRate={playbackRate}
              chapters={chapters}
              bookmarks={bookmarks}
              onPlayPause={handlePlayPause}
              onSeek={handleSeek}
              onSkip={handleSkip}
              onPlaybackRateChange={handlePlaybackRateChange}
              onAddBookmark={handleAddBookmark}
              onUpdateBookmarkNote={handleUpdateBookmarkNote}
              onDeleteBookmark={handleDeleteBookmark}
              onInfoClick={onInfoClick}
            />
          )}

          {activeTab === 'transcript' && transcript && (
            <div className="h-full pb-16">
              <TranscriptView transcript={transcript} currentTime={currentTime} onSeek={handleSeek} />
            </div>
          )}

          {activeTab === 'pdf' && pdfStreamPath && (
            <div className="h-full pb-16">
              <PdfReader
                url={pdfStreamPath}
                title={`${title} - PDF`}
                episodeId={pdfEpisode?.episode_id ?? 0}
                numPages={0}
                onClose={() => setActiveTab('player')}
                onInfoClick={onInfoClick}
                offlineBlobUrl={isPdfOffline ? (pdfOfflineUrl ?? undefined) : undefined}
              />
            </div>
          )}

          {/* PDF/テキストタブ表示中のミニプレーヤー */}
          {(activeTab === 'pdf' || activeTab === 'transcript') && (
            <MiniPlayer
              title={title}
              playing={playing}
              currentTime={currentTime}
              duration={duration}
              onPlayPause={handlePlayPause}
              onSkip={handleSkip}
            />
          )}
        </div>

        {!progressReady && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-sm">
            <Spinner size={32} />
            <p className="text-xs text-muted-foreground">再生位置を復元しています...</p>
          </div>
        )}
      </div>
    </div>
  )
}
